import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FaInstagram, FaTwitter, FaYoutube, FaFacebook, FaLinkedin, FaTiktok, FaSpotify, FaApple, FaEnvelope, FaGlobe, FaExternalLinkAlt, FaPlay, FaFileAlt, FaDownload, FaLink, FaBookOpen, FaCalendarAlt, FaMapMarkerAlt, FaShoppingCart, FaUsers, FaMicrophone, FaNewspaper, FaLock, FaImage, FaHeadphones, FaBuilding, FaPhone, FaUser } from 'react-icons/fa';
import './LinkHub.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  instagram: FaInstagram, twitter: FaTwitter, x: FaTwitter,
  youtube: FaYoutube, facebook: FaFacebook, linkedin: FaLinkedin,
  tiktok: FaTiktok, spotify: FaSpotify, apple: FaApple,
  email: FaEnvelope, website: FaGlobe, threads: FaTwitter,
};

const trackClick = (slug, link) => {
  fetch(`${API_URL}/api/link-hub/track/click`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ hub_slug: slug, link_id: link.id, link_url: link.url, link_label: link.label, referrer: document.referrer, user_agent: navigator.userAgent }),
  }).catch(() => {});
};

const appendUtm = (url, utm) => {
  if (!utm) return url;
  try { const u = new URL(url); utm.split('&').forEach(p => { const [k, v] = p.split('='); if (k) u.searchParams.set(k, v || ''); }); return u.toString(); }
  catch { return url; }
};

const LinkHub = () => {
  const { slug } = useParams();
  const [hub, setHub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [gate, setGate] = useState(null); // { link } when a gated link is clicked

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/link-hub/pages/${slug}`);
        if (!r.ok) throw new Error();
        setHub(await r.json());
      } catch { setError(true); }
      setLoading(false);
    })();
  }, [slug]);

  if (loading) return (
    <div className="link-hub-page link-hub-skeleton-page">
      <div className="hub-container">
        <div className="skeleton-avatar" />
        <div className="skeleton-bar" style={{ width: 200, height: 24, margin: '0 auto' }} />
        <div className="skeleton-bar" style={{ width: 260, height: 16, margin: '12px auto 0' }} />
        <div className="skeleton-social-row">{[1, 2, 3, 4, 5].map(i => <div key={i} className="skeleton-social-icon" />)}</div>
        {[1, 2, 3, 4].map(i => <div key={i} className="skeleton-link-card" />)}
      </div>
    </div>
  );

  if (error || !hub) return (
    <div className="link-hub-page link-hub-error">
      <div className="hub-container" style={{ textAlign: 'center' }}>
        <h1 className="error-code">404</h1>
        <p className="error-message">Link hub not found</p>
        <a href="/" className="error-home-link">Return Home</a>
      </div>
    </div>
  );

  return (
    <>
      <Helmet>
        <title>{hub.title || hub.header?.display_name || 'Link Hub'}</title>
        <meta name="description" content={hub.header?.bio || ''} />
        {hub.noindex && <meta name="robots" content="noindex,nofollow" />}
      </Helmet>
      <LinkHubRenderer hub={hub} slug={slug} onGateClick={(link) => setGate({ link })} />
      {gate && <GateModal slug={slug} link={gate.link} theme={hub.theme || {}} onClose={() => setGate(null)} />}
    </>
  );
};

export const LinkHubRenderer = ({ hub, slug, isPreview = false, onLinkClick, onGateClick }) => {
  if (!hub) return null;
  const t = hub.theme || {};
  const h = hub.header || {};
  const links = (hub.links || []).filter(l => l.enabled);
  const featured = links.filter(l => l.featured);
  const regular = links.filter(l => !l.featured);
  const socials = (hub.social_links || []).filter(s => s.enabled && s.url);
  const utm = hub.utm_params || '';
  const contacts = (hub.contacts || []).filter(c => c.enabled !== false && (c.name || c.role || c.email || c.phone || c.linkedin));
  const contactsPos = hub.contacts_position || 'bottom';
  const contactsLayout = hub.contacts_layout || 'grid';
  const contactsHeading = hub.contacts_heading || 'Contacts';
  const blocks = (hub.blocks || []).filter(b => b.enabled !== false && (b.type === 'newsletter' || b.type === 'html'));
  const blocksTop = blocks.filter(b => (b.position || 'bottom') === 'top');
  const blocksBottom = blocks.filter(b => (b.position || 'bottom') !== 'top');

  const wrapperStyle = {
    '--hub-accent': t.accent_color || '#004534',
    '--hub-accent-bright': t.accent_bright || '#00d99a',
  };
  if (t.background_type === 'gradient') wrapperStyle.background = `linear-gradient(${t.gradient_angle || 180}deg, ${t.gradient_start || '#0d3028'} 0%, ${t.gradient_end || '#050f0c'} 100%)`;
  else if (t.background_type === 'solid') wrapperStyle.background = t.background_color || '#0a1a16';
  else if (t.background_type === 'image' && t.background_image) { wrapperStyle.backgroundImage = `url(${t.background_image})`; wrapperStyle.backgroundSize = 'cover'; wrapperStyle.backgroundPosition = 'center'; }

  const cardStyle = t.card_background ? { '--custom-card-bg': t.card_background } : {};
  const btnStyle = t.button_style || 'glass';
  const themeMode = t.theme_mode === 'light' ? 'light' : 'dark';

  return (
    <div
      className={`link-hub-page theme-${themeMode} ${isPreview ? 'is-preview' : ''}`}
      style={wrapperStyle}
      data-hub-theme={themeMode}
      data-testid="link-hub-page"
      onClickCapture={isPreview ? (e) => { e.preventDefault(); e.stopPropagation(); } : undefined}
    >
      {t.show_grain_texture && <div className="hub-grain-overlay" />}
      <div className="hub-glow" />

      <div className="hub-container">
          {h.banner_image && h.show_banner !== false && (
            <div className="hub-banner-wrapper" data-testid="hub-banner">
              <img src={h.banner_image.startsWith('/') ? `${API_URL}${h.banner_image}` : h.banner_image} alt="" className="hub-banner-img" />
            </div>
          )}

          {h.logos?.filter(l => l.enabled !== false).length > 0 && (
            <div className="hub-logos-row" data-testid="hub-logos">
              {h.logos.filter(l => l.enabled !== false).map((logo, i) => (
                <img key={logo.id || i} src={logo.url.startsWith('/') ? `${API_URL}${logo.url}` : logo.url}
                  alt={logo.alt || ''} className="hub-logo-img"
                  style={{ height: `${logo.size || 52}px`, animationDelay: `${0.05 + i * 0.08}s` }} />
              ))}
            </div>
          )}

          <header className={`hub-header ${h.banner_image && h.show_banner !== false ? 'has-banner' : ''} ${h.logos?.filter(l => l.enabled !== false).length > 0 ? 'has-logos' : ''}`}>
            {h.avatar_image && h.show_avatar !== false && (
              <div className="hub-avatar-wrapper">
                <img src={h.avatar_image.startsWith('/') ? `${API_URL}${h.avatar_image}` : h.avatar_image} alt={h.display_name || ''} className="hub-avatar" />
              </div>
            )}
            {h.display_name && <h1 className="hub-display-name" style={{ color: t.title_color || undefined }}>{h.display_name}</h1>}
            {h.bio && <p className="hub-bio" style={{ color: t.bio_color || undefined }}>{h.bio}</p>}
          </header>

          {t.show_social_row !== false && socials.length > 0 && (
            <div className="hub-social-row">
              {socials.map((s, i) => {
                const Icon = ICON_MAP[s.platform?.toLowerCase()] || FaExternalLinkAlt;
                return (
                  <a key={s.id || i} href={s.platform === 'email' ? `mailto:${s.url}` : s.url}
                    target="_blank" rel="noopener noreferrer" className="hub-social-icon"
                    title={s.platform} data-testid={`social-${s.platform}`} style={{ animationDelay: `${0.1 + i * 0.05}s` }}>
                    <Icon />
                  </a>
                );
              })}
            </div>
          )}

          {contacts.length > 0 && contactsPos === 'top' && (
            <ContactsSection contacts={contacts} heading={contactsHeading} layout={contactsLayout} theme={t} />
          )}

          {blocksTop.map(b => <HubBlock key={b.id} block={b} slug={slug} theme={t} isPreview={isPreview} />)}

          {featured.length > 0 && (
            <div className="hub-featured-section" style={{ gap: `${t.card_spacing || 16}px` }}>
              {featured.map((link, i) => (
                <Card key={link.id} link={link} theme={t} btn={btnStyle} cardStyle={cardStyle} delay={0.2 + i * 0.08} slug={slug} utm={utm} featured onGateClick={onGateClick} />
              ))}
            </div>
          )}

          <div className="hub-links-stack" style={{ gap: `${t.card_spacing || 16}px` }}>
            {regular.map((link, i) => (
              <Card key={link.id} link={link} theme={t} btn={btnStyle} cardStyle={cardStyle} delay={0.3 + i * 0.06} slug={slug} utm={utm} onGateClick={onGateClick} />
            ))}
          </div>

          {contacts.length > 0 && contactsPos === 'bottom' && (
            <ContactsSection contacts={contacts} heading={contactsHeading} layout={contactsLayout} theme={t} />
          )}

          {blocksBottom.map(b => <HubBlock key={b.id} block={b} slug={slug} theme={t} isPreview={isPreview} />)}

          <footer className="hub-footer">
            <span>{hub.footer_text || hub.title || ''}</span>
          </footer>
      </div>
    </div>
  );
};

const ICON_BY_KEY = {
  external: FaExternalLinkAlt, play: FaPlay, download: FaDownload, file: FaFileAlt,
  book: FaBookOpen, newspaper: FaNewspaper, globe: FaGlobe, calendar: FaCalendarAlt,
  map: FaMapMarkerAlt, cart: FaShoppingCart, users: FaUsers, mic: FaMicrophone,
  headphones: FaHeadphones, lock: FaLock, image: FaImage, building: FaBuilding,
  email: FaEnvelope, linkedin: FaLinkedin, instagram: FaInstagram, twitter: FaTwitter,
  facebook: FaFacebook, youtube: FaYoutube, spotify: FaSpotify, tiktok: FaTiktok,
};

const getLinkIcon = (link) => {
  // If user explicitly set an icon, use it
  if (link.icon && link.icon !== 'auto' && ICON_BY_KEY[link.icon]) return ICON_BY_KEY[link.icon];
  // Auto-detect from URL/label
  const url = (link.url || '').toLowerCase();
  const label = (link.label || '').toLowerCase();
  if (url.includes('vimeo.com') || url.includes('youtube.com') || label.includes('watch') || label.includes('film') || label.includes('video')) return FaPlay;
  if (url.includes('linkedin.com') || label.includes('linkedin')) return FaLinkedin;
  if (label.includes('download') || url.includes('dropbox.com')) return FaDownload;
  if (label.includes('read') || label.includes('report') || url.endsWith('.pdf')) return FaFileAlt;
  if (url.includes('instagram.com')) return FaInstagram;
  if (url.includes('twitter.com') || url.includes('x.com')) return FaTwitter;
  if (url.includes('facebook.com')) return FaFacebook;
  if (url.includes('spotify.com')) return FaSpotify;
  if (url.includes('tiktok.com')) return FaTiktok;
  return FaExternalLinkAlt;
};

const Card = ({ link, theme, btn, cardStyle, delay, slug, utm, featured, onGateClick }) => {
  const cs = link.coming_soon;
  const gated = link.is_gated && !cs;
  const Tag = (cs || gated) ? 'div' : 'a';
  const href = (cs || gated) ? undefined : appendUtm(link.url, utm);
  const handleGate = (e) => {
    e.preventDefault();
    if (onGateClick) onGateClick(link);
  };
  const props = cs
    ? {}
    : gated
      ? { onClick: handleGate, role: 'button', tabIndex: 0, style: { cursor: 'pointer' } }
      : { href, target: link.open_in_new_tab ? '_blank' : '_self', rel: 'noopener noreferrer', onClick: () => trackClick(slug, link) };
  const LinkIcon = gated ? FaLock : getLinkIcon(link);

  return (
    <Tag {...props} className={`hub-link ${btn} ${cs ? 'coming-soon' : ''} ${featured ? 'featured' : ''} ${gated ? 'gated' : ''}`}
      style={{ ...cardStyle, animationDelay: `${delay}s`, ...(props.style || {}) }} data-testid={`hub-link-${link.id}`}>
      <span className="hub-link-icon"><LinkIcon /></span>
      <div className="hub-link-content">
        <span className="hub-link-title" style={{ color: theme.link_title_color || undefined }}>{link.label}</span>
        {link.subtext && <span className="hub-link-subtitle" style={{ color: theme.link_subtitle_color || undefined }}>{link.subtext}</span>}
      </div>
      <span className="hub-link-arrow">{gated ? <FaLock /> : <FaExternalLinkAlt />}</span>
      {cs && <span className="hub-badge-coming-soon" style={{ backgroundColor: theme.badge_bg_color || '#fbbf24', color: theme.badge_text_color || '#1f2937' }}>Coming Soon</span>}
      {featured && !cs && <span className="hub-featured-indicator" />}
    </Tag>
  );
};

// Gate modal for gated resources — captures email, POSTs to /unlock, then redirects to resource_url.
const GateModal = ({ slug, link, theme, onClose }) => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  const submit = async (e) => {
    e?.preventDefault?.();
    setError('');
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError('Please enter a valid email');
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/link-hub/pages/${slug}/unlock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          link_id: link.id,
          email: trimmed,
          first_name: firstName.trim(),
          referrer: document.referrer,
          user_agent: navigator.userAgent,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Unlock failed');
      setSuccess(data);
      // Open the resource in a new tab
      if (data.resource_url) window.open(data.resource_url, '_blank', 'noopener');
    } catch (err) {
      setError(err.message || 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => { if (e.key === 'Escape') onClose(); };
  useEffect(() => {
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, []);

  return (
    <div className="hub-gate-backdrop" onClick={onClose} data-testid="hub-gate-modal">
      <div className="hub-gate-modal" onClick={(e) => e.stopPropagation()}>
        <button className="hub-gate-close" onClick={onClose} aria-label="Close" data-testid="hub-gate-close">×</button>
        <div className="hub-gate-icon"><FaLock /></div>
        <h2 className="hub-gate-title">{link.gate_headline || 'Unlock this resource'}</h2>
        <p className="hub-gate-desc">{link.gate_description || `Enter your email to receive "${link.label}".`}</p>
        {success ? (
          <div className="hub-gate-success">
            <p className="hub-gate-success-title">✓ You&apos;re in!</p>
            <p className="hub-gate-success-body">
              {success.email_sent
                ? 'Check your inbox — we just emailed you the resource.'
                : 'The resource has opened in a new tab.'}
            </p>
            <button className="hub-gate-cta" onClick={onClose} data-testid="hub-gate-done">Close</button>
          </div>
        ) : (
          <form onSubmit={submit} className="hub-gate-form">
            <input
              type="text"
              placeholder="First name (optional)"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="hub-gate-input"
              autoComplete="given-name"
              data-testid="hub-gate-firstname"
            />
            <input
              type="email"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="hub-gate-input"
              autoComplete="email"
              required
              autoFocus
              data-testid="hub-gate-email"
            />
            {error && <div className="hub-gate-error" data-testid="hub-gate-error">{error}</div>}
            <button type="submit" className="hub-gate-cta" disabled={busy} data-testid="hub-gate-submit">
              {busy ? 'Sending…' : (link.gate_submit_label || 'Get access')}
            </button>
            <p className="hub-gate-fineprint">We&apos;ll email you the resource. Unsubscribe anytime.</p>
          </form>
        )}
      </div>
    </div>
  );
};

// Newsletter signup / custom HTML block, rendered inline on the public page.
const HubBlock = ({ block, slug, theme, isPreview }) => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState('');

  if (block.type === 'html') {
    return (
      <section className="hub-html-block" data-testid={`hub-html-block-${block.id}`}>
        <div dangerouslySetInnerHTML={{ __html: block.html || '' }} />
      </section>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    const t = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t)) { setErr('Please enter a valid email'); return; }
    if (isPreview) { setMsg(block.success_message || "You're in!"); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/link-hub/pages/${slug}/newsletter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ block_id: block.id, email: t, first_name: firstName.trim() }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Signup failed');
      setMsg(data.message || block.success_message || "Thanks — you're subscribed!");
      setEmail(''); setFirstName('');
    } catch (e2) {
      setErr(e2.message || 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="hub-newsletter-block" data-testid={`hub-newsletter-${block.id}`}>
      <div className="hub-newsletter-inner">
        <h3 className="hub-newsletter-headline" style={{ color: theme.title_color || undefined }}>{block.headline || 'Stay in the loop'}</h3>
        {block.description && <p className="hub-newsletter-desc" style={{ color: theme.bio_color || undefined }}>{block.description}</p>}
        {msg ? (
          <div className="hub-newsletter-success" data-testid={`hub-newsletter-success-${block.id}`}>✓ {msg}</div>
        ) : (
          <form onSubmit={submit} className="hub-newsletter-form">
            <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="First name (optional)" className="hub-newsletter-input" autoComplete="given-name" data-testid={`hub-newsletter-firstname-${block.id}`} />
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="hub-newsletter-input" required autoComplete="email" data-testid={`hub-newsletter-email-${block.id}`} />
            {err && <div className="hub-newsletter-error">{err}</div>}
            <button type="submit" className="hub-newsletter-btn" disabled={busy} data-testid={`hub-newsletter-submit-${block.id}`}>
              {busy ? 'Sending…' : (block.button_label || 'Subscribe')}
            </button>
          </form>
        )}
      </div>
    </section>
  );
};

const ContactsSection = ({ contacts, heading, layout, theme }) => {
  const isList = layout === 'list';
  return (
    <section className={`hub-contacts-section ${isList ? 'list' : 'grid'}`} data-testid="hub-contacts-section">
      {heading && (
        <h2 className="hub-contacts-heading" style={{ color: theme.title_color || undefined }}>{heading}</h2>
      )}
      <div className={isList ? 'hub-contacts-list' : 'hub-contacts-grid'}>
        {contacts.map((c, i) => {
          const photoSrc = c.photo?.startsWith('/') ? `${API_URL}${c.photo}` : c.photo;
          return (
            <article key={c.id || i} className="hub-contact-card" style={{ animationDelay: `${0.35 + i * 0.06}s` }} data-testid={`contact-card-${i}`}>
              <div className="hub-contact-avatar">
                {photoSrc ? <img src={photoSrc} alt={c.name || ''} /> : <FaUser />}
              </div>
              <div className="hub-contact-body">
                {c.name && <h3 className="hub-contact-name" style={{ color: theme.link_title_color || undefined }}>{c.name}</h3>}
                {c.role && <p className="hub-contact-role" style={{ color: theme.link_subtitle_color || undefined }}>{c.role}</p>}
                <div className="hub-contact-links">
                  {c.email && (
                    <a href={`mailto:${c.email}`} className="hub-contact-link" title={c.email}>
                      <FaEnvelope /><span>{c.email}</span>
                    </a>
                  )}
                  {c.phone && (
                    <a href={`tel:${c.phone.replace(/\s+/g, '')}`} className="hub-contact-link" title={c.phone}>
                      <FaPhone /><span>{c.phone}</span>
                    </a>
                  )}
                  {c.linkedin && (
                    <a href={c.linkedin} target="_blank" rel="noopener noreferrer" className="hub-contact-link" title="LinkedIn">
                      <FaLinkedin /><span>LinkedIn</span>
                    </a>
                  )}
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
};

export default LinkHub;
